import React, { useEffect, useState } from 'react';
import { Card, Table, Tag, Space, Typography, Button } from 'antd';
import { ExpandOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import { VIP_STATUS_LABELS, VIP_STATUS_COLORS, VipStatus } from '../types';
import apiClient from '../services/api';

const { Title } = Typography;

// Interface for PRO user data
interface ProUser {
  account_id: string;
  full_name: string;
  email: string;
  specialty: string;
  prefecture: string;
  clinic_name: string;
  vip_status: number;
  subscription_product: string;
  first_subscription_time: string;
  created_at: string;
}

interface ProUsersResponse {
  pro_users: ProUser[];
  total_count: number;
}

const ProUsersList: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ProUser[]>([]);

  useEffect(() => {
    fetchProUsers();
  }, []);

  const fetchProUsers = async () => {
    try {
      setLoading(true);
      const response: ProUsersResponse = await apiClient.get('/api/analytics/pro-users');
      console.log('PRO users response:', response);
      setData(response.pro_users);
    } catch (error) {
      console.error('Failed to fetch PRO users:', error);
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnsType<ProUser> = [
    {
      title: 'ユーザー情報',
      dataIndex: 'full_name',
      key: 'full_name',
      width: 200,
      render: (name: string, record: ProUser) => (
        <div>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '4px',
            marginBottom: '3px',
            flexWrap: 'wrap'
          }}>
            <span style={{ fontWeight: 'bold', fontSize: '12px' }}>
              {name || `ユーザー${record.account_id.slice(-8)}`}
            </span>
            <Tag 
              color={VIP_STATUS_COLORS[record.vip_status as VipStatus]}
              style={{ fontSize: '9px', padding: '0 4px', lineHeight: '16px' }}
            >
              {VIP_STATUS_LABELS[record.vip_status as VipStatus]}
            </Tag>
          </div>
          <div style={{ fontSize: '10px', color: '#666', marginBottom: '2px' }}>
            {record.email}
          </div>
          <div style={{ fontSize: '10px', color: '#1890ff', cursor: 'pointer' }}
               onClick={() => {
                 if (record.clinic_name && record.clinic_name !== 'なし') {
                   const searchQuery = record.prefecture ? 
                     `${record.clinic_name} ${record.prefecture}` : 
                     record.clinic_name;
                   const googleSearchUrl = `https://www.google.com/search?q=${encodeURIComponent(searchQuery)}`;
                   window.open(googleSearchUrl, '_blank');
                 }
               }}>
            📍 {record.clinic_name || 'なし'} ({record.prefecture || '不明'})
          </div>
        </div>
      ),
    },
    {
      title: '専業',
      dataIndex: 'specialty',
      key: 'specialty',
      width: 90,
      render: (text: string) => <span style={{ fontSize: '12px' }}>{text}</span>,
    },
    {
      title: '初回課金日',
      dataIndex: 'first_subscription_time',
      key: 'first_subscription_time',
      width: 100,
      render: (date: string) => <span style={{ fontSize: '11px' }}>{date || '不明'}</span>,
    },
    {
      title: '登録日',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 85,
      render: (date: string) => <span style={{ fontSize: '11px' }}>{date || '不明'}</span>,
    },
    {
      title: '購読商品',
      dataIndex: 'subscription_product',
      key: 'subscription_product',
      width: 85,
      align: 'center',
      render: (product: string) => {
        let color = 'default';
        if (product === '月费' || product === '月額') {
          color = 'blue';
        } else if (product === '年费' || product === '年額') {
          color = 'green';
        } else if (product === '存储' || product === 'ストレージ') {
          color = 'orange';
        }
        
        return (
          <Tag color={color} style={{ fontSize: '10px', padding: '0 4px' }}>
            {product || '不明'}
          </Tag>
        );
      },
    },
  ];

  return (
    <Card 
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <Title level={4} style={{ margin: 0 }}>PROユーザー一覧</Title>
            <span style={{ color: '#999', fontSize: '14px' }}>PRO Users List</span>
          </Space>
          <Button
            type="text"
            icon={<ExpandOutlined />}
            onClick={() => window.open('/fullscreen/pro-users', '_blank')}
            style={{ marginLeft: 'auto' }}
          />
        </div>
      }
      style={{ height: '100%', minHeight: '400px' }}
    >
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Table
          loading={loading}
          columns={columns}
          dataSource={data}
          rowKey="account_id"
          pagination={{
            pageSize: 4,
            showSizeChanger: false,
            showTotal: (total) => `全 ${total} 件`,
            size: 'small',
            style: { marginBottom: 0, marginTop: '8px' }
          }}
          scroll={{ x: 'max-content' }}
          size="small"
          style={{ fontSize: '12px' }}
        />
      </div>
    </Card>
  );
};

export default ProUsersList;